import { useMemo, useState } from "react"
import { Box, Button, IconButton, Stack, Typography } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { Else } from "lib/@components/else"
import { If } from "lib/@components/switch"
import { RightDrawer } from "lib/@components/drawer/right-drawer"
import { noChange, useRefresh } from "lib/@hooks/useRefresh"
import { cloneDeep } from "lib/clone"
import Iconify from "minimals-template/components/Iconify"
import { EditMappingFormBody } from "./edit-mapping-form-body"
import { isEditMappingInvalid } from "./is-edit-mapping-invalid"

export function EditMappingDrawer({ open, mapping, allLines, tradeVocabulary, onClose, onSubmit }) {
    const refresh = useRefresh(noChange)
    const target = useMemo(buildInitialTarget, [mapping])
    const [saving, setSaving] = useState(false)
    const isEdit = !!mapping?.tradeId

    if (!open) {
        return <RightDrawer open={open} setOpen={handleSetOpen} onClose={onClose} />
    }

    return (
        <RightDrawer open={open} setOpen={handleSetOpen} onClose={onClose}>
            <Bound target={target} refresh={refresh}>
                <Stack data-cy="edit-mapping-drawer" spacing={2} sx={{ height: "100%" }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                            <Typography variant="h6">
                                <If value={isEdit}>
                                    Edit Skill Mapping<Else>Add Trade</Else>
                                </If>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Assign skills to a trade in this skill map.
                            </Typography>
                        </Stack>
                        <IconButton aria-label="Close" size="small" onClick={onClose} data-cy="edit-mapping-close">
                            <Iconify icon="eva:close-fill" />
                        </IconButton>
                    </Stack>

                    <Box sx={{ flex: 1, overflow: "auto", pr: 0.5 }}>
                        <EditMappingFormBody allLines={allLines} tradeVocabulary={tradeVocabulary} />
                    </Box>

                    <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                        <Button onClick={onClose} data-cy="edit-mapping-cancel">
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disabled={saving || isEditMappingInvalid(target, mapping)}
                            onClick={handleSubmit}
                            data-cy="edit-mapping-save"
                        >
                            Save
                        </Button>
                    </Stack>
                </Stack>
            </Bound>
        </RightDrawer>
    )

    function buildInitialTarget() {
        return cloneDeep({
            tradeName: mapping?.tradeName || "",
            tradeId: mapping?.tradeId || null,
            skills: mapping?.skills || [],
        })
    }

    function handleSetOpen(value) {
        if (value) return
        onClose?.()
    }

    async function handleSubmit() {
        if (isEditMappingInvalid(target, mapping)) return
        setSaving(true)
        try {
            await onSubmit?.(cloneDeep(target))
        } finally {
            setSaving(false)
        }
    }
}
